import { useSyncExternalStore } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

type Kind = "success" | "error" | "info" | "warning";
interface ToastItem {
  id: number;
  kind: Kind;
  title: string;
  description?: string;
  /** Masalan "Qaytarish": bosilsa bajariladi va xabar yopiladi */
  action?: ToastAction;
}

export interface ToastAction {
  label: string;
  onClick: () => void;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function toast(kind: Kind, title: string, description?: string, ttl = 5000, action?: ToastAction) {
  const id = nextId++;
  items = [...items, { id, kind, title, description, action }];
  emit();
  window.setTimeout(() => dismiss(id), ttl);
  return id;
}
toast.success = (t: string, d?: string) => toast("success", t, d);
toast.error = (t: string, d?: string) => toast("error", t, d, 8000);
toast.info = (t: string, d?: string) => toast("info", t, d);
toast.warning = (t: string, d?: string) => toast("warning", t, d, 7000);
toast.undo = (t: string, action: ToastAction) => toast("info", t, undefined, 7000, action);

export function dismiss(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

/** Tur -- chap tomondagi nuqta rangi (chiziq emas: redesign v2) */
const dots: Record<Kind, string> = {
  success: "bg-success",
  error: "bg-danger",
  info: "bg-primary",
  warning: "bg-warning",
};

export function Toaster() {
  const list = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => items,
  );
  if (list.length === 0) return null;
  return (
    <div className="pointer-events-none fixed right-4 bottom-4 z-[100] flex w-[360px] max-w-[calc(100vw-32px)] flex-col gap-2">
      {list.map((t) => (
        <div key={t.id} role="status" className="pop-in pointer-events-auto rounded-2xl border border-border/60 bg-surface p-3.5 shadow-menu">
          <div className="flex items-start gap-3">
            <span className={cn("mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full", dots[t.kind])} aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold">{t.title}</div>
              {t.description && <div className="mt-0.5 text-[13px] text-ink-soft">{t.description}</div>}
            </div>
            {t.action && (
              <button
                onClick={() => {
                  dismiss(t.id);
                  t.action?.onClick();
                }}
                className="shrink-0 text-[13px] font-semibold text-primary hover:underline"
              >
                {t.action.label}
              </button>
            )}
            <button onClick={() => dismiss(t.id)} className="-m-1 grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted hover:bg-fill hover:text-ink" aria-label="×">
              <X size={15} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
